import { debugTracking } from '$lib/debug';
import { setTrackingProperty } from '$lib/helper';

import events from './events';
import { getNowISO } from './helper';
import { getTrackingService } from './TrackingServiceInstance';
import { page, track, trackQuantityEvent, trackUniqueEvent } from './v4.calls';
import psProperties from './v4.ps-properties';
import { getVisitorProperties, updateVisitorProperties } from './v4.visitor-properties';

export const campaignLoad = (pageSlug: string, pageName: string) => {
    debugTracking('-> V4 campaignLoad', pageSlug, pageName);

    const { utm, custom, thirdParty } = getTrackingService().queryParams;

    trackQuantityEvent(events.campaignLoad, {
        pageSlug,
        utm,
        custom,
        thirdParty,
    });
};

export const pageLoad = (
    pageId: string,
    pageName: string,
    pageSlug: string,
    isLastPage: boolean,
    fromPageId: string | null,
    fromPageName: string | null,
) => {
    debugTracking('-> V4 pageLoad', pageId, pageName, pageSlug, isLastPage);

    const properties = {
        pageId,
        pageName,
        pageSlug,
        isLastPage,
        fromPageId,
        fromPageName,
    };
    // campaignLoad is now called explicitly via tracking.trackCampaignLoad()
    // at the call site (Page.svelte) to make the event boundary visible.
    page(pageName, properties);
    // TODO CF: find out if we need this
    // trackQuantityEvent(events.pageLoad, properties);

    trackUniqueEvent(`${events.pageLoad}: ${pageSlug}`, properties);
    const visitorProperties = getVisitorProperties();

    // Check for `sessionId` query parameter to determine if the user was redirected to the last page
    // (e.g., after a payment process)
    const sessionId = new URLSearchParams(window.location.search).get('sessionId');

    // Only "completes" when NOT started on last page OR was redirected to last page
    if (isLastPage && (visitorProperties[psProperties.startSlug] !== pageSlug || sessionId)) {
        campaignComplete(pageName);
    }
};

export const buttonClick = (buttonClickId: string) => {
    debugTracking('-> V4 buttonClick', buttonClickId);

    const properties = {
        clickId: buttonClickId,
    };

    trackQuantityEvent(events.buttonClick, properties);
    trackUniqueEvent(`${events.buttonClick}: ${buttonClickId}`, properties);
};

export const imageClick = (imageClickId: string) => {
    debugTracking('-> V4 imageClick', imageClickId);

    const properties = {
        clickId: imageClickId,
    };

    trackQuantityEvent(events.imageClick, properties);
    trackUniqueEvent(`${events.imageClick}: ${imageClickId}`, properties);
};

export const cookiesAccept = (integrations: string[]) => {
    debugTracking('-> V4 cookiesAccept', integrations);

    trackQuantityEvent(events.cookiesAccept, {
        integrations,
    });

    updateVisitorProperties({ [psProperties.cookies]: integrations.sort().join('; ') });
};

interface AnswerClickedQuestion {
    text: string;
    fieldName: string;
}
interface AnswerClickedAnswer {
    text: string;
    clickId: string;
}
export const answerClicked = (
    question: AnswerClickedQuestion,
    answer: AnswerClickedAnswer,
    campaignId?: string,
    uniqueEventPrefix: string = events.answerClick,
) => {
    debugTracking('-> V4 answerClicked', question, answer);

    const properties = {
        questionText: question.text,
        answerText: answer.text,
        fieldName: question.fieldName,
    };

    trackQuantityEvent(events.answerClick, properties);
    trackUniqueEvent(`${uniqueEventPrefix}: ${answer.clickId}`, properties);
    updateVisitorProperties({ [question.fieldName]: answer.text });

    // For personalization
    setTrackingProperty(question.fieldName, answer.text, campaignId);
};

export const formSubmit = (
    formValues: Record<string, string>,
    submitButtonClickId: string,
    options?: { suppressConversion?: boolean },
) => {
    debugTracking('-> V4 formSubmit', formValues, submitButtonClickId);

    trackQuantityEvent(events.formSubmit, {
        ...formValues,
    });

    buttonClick(submitButtonClickId);

    updateVisitorProperties(formValues, options);
};

export const multipleChoiceSubmit = (
    question: AnswerClickedQuestion,
    answer: AnswerClickedAnswer,
    campaignId: string,
) => {
    debugTracking('-> V4 answerClicked', question, answer);

    answerClicked(question, answer, campaignId, events.buttonClick);
};

export const convert = () => {
    debugTracking('-> V4 convert');

    track(events.convert, {
        ...getVisitorProperties(),
    });
};

export const campaignComplete = (pageName: string) => {
    debugTracking('-> V4 campaignComplete', pageName);

    updateVisitorProperties({
        [psProperties.isCompleted]: true,
        [psProperties.completedAt]: getNowISO(),
        [psProperties.result]: pageName,
    });

    track(events.campaignComplete, {
        ...getVisitorProperties(),
    });
};

export const calendlyBooking = () => {
    debugTracking('-> V4 calendlyBooking');

    trackQuantityEvent(events.calendlyBook);

    updateVisitorProperties({
        [psProperties.calendlyBook]: true,
    });
};

export const calBooking = () => {
    debugTracking('-> V4 calBooking');

    trackQuantityEvent(events.calBook);

    updateVisitorProperties({
        [psProperties.calBook]: true,
    });
};

export const ghlBooking = () => {
    debugTracking('-> V4 ghlBooking');

    trackQuantityEvent(events.ghlBook);

    updateVisitorProperties({
        [psProperties.ghlBook]: true,
    });
};

export const hubspotBooking = () => {
    debugTracking('-> V4 hubspotBooking');

    trackQuantityEvent(events.hubspotBook);

    updateVisitorProperties({
        [psProperties.hubspotBook]: true,
    });
};

export const htmlEmbedFirstInteraction = (props: { embedType: 'sandboxed' | 'native' }) => {
    debugTracking('-> V4 htmlEmbedFirstInteraction');

    trackQuantityEvent(events.htmlEmbedFirstInteraction, {
        sandboxedEmbed: props.embedType === 'sandboxed',
        nativeEmbed: props.embedType === 'native',
    });
};
